import React, { useCallback, useEffect } from 'react';
import { array, bool, func, shape, string } from 'prop-types';
import { isEmpty } from 'lodash';
import useApi from '../../../hooks/useApi';
import template from 'url-template';
import { toSentence } from '../../../helpers/strings';

import TagSelect from '../../FormFields/TagSelect';
import {
    isTargetSelected,
    optionsTransform,
    siteOptionsTransform,
    targetChangeHandler,
    RULE_TARGET_TO_TYPE
} from './targetMappings';

const BuildingTypesUrl = template.parse('/building-types{?limit}');
const LIMIT = 10000;
const SitesUrl = template.parse('/companies/{companyId}/sites{?limit,sort}');

const SitesTargetSelect = ({
    companyId,
    disabled,
    onPatch,
    values,
    ...props
}) => {
    const [buildingTypes, getBuildingTypes] = useApi(() => ({
        url: BuildingTypesUrl.expand({ limit: LIMIT })
    }));
    const [sites, getSites] = useApi(() => ({
        url: SitesUrl.expand({
            companyId,
            limit: LIMIT,
            sort:  'name^asc'
        })
    }));

    useEffect(() => {
        getBuildingTypes();
        getSites();
        return () => {
            buildingTypes.cancel();
            sites.cancel();
        };
    }, []);

    useEffect(() => {
        getSites();
    }, [companyId]);

    const handleChange = useCallback(
        targetChangeHandler(onPatch, {
            targetBuildingTypes: [],
            targetSites:         []
        })
    );

    const options = [
        {
            error:     buildingTypes.error,
            isLoading: buildingTypes.loading,
            items:     optionsTransform(buildingTypes.data),
            name:      'Building Types',
            itemProps: {
                type: RULE_TARGET_TO_TYPE.targetBuildingTypes
            }
        },
        {
            error:     sites.error,
            isLoading: sites.loading,
            items:     siteOptionsTransform(sites.data),
            name:      'Sites',
            itemProps: {
                type: RULE_TARGET_TO_TYPE.targetSites
            }
        }
    ];

    let formattedSites;

    // K2AINT-485: this code will add the clientName to the display if neccessary
    if (isEmpty(sites.data)) {
        formattedSites = values.targetSites;
    }
    else {
        formattedSites = values.targetSites.map(ts => {
            const siteData = sites.data.find(s => s.id === ts.nodeId);
            let displayName = ts.name;
            if (siteData && !displayName.includes(siteData.clientName + '/')) {
                displayName = siteData.clientName + '/' + ts.name;
            }
            return {
                ...ts,
                name: displayName 
            };
        });
    }

    const value = [...values.targetBuildingTypes, ...formattedSites];
    const hasError = Boolean(buildingTypes.error) || Boolean(sites.error);
    const errorText = toSentence([buildingTypes.error, sites.error]);

    return (
        <TagSelect
            disabled={disabled}
            error={hasError}
            grouped
            isItemSelected={isTargetSelected}
            kickerText="Add any combination of Building Types and Specific Sites that this Rule will be applied to"
            helperText={errorText}
            label="Sites"
            name="sites-target"
            onChange={handleChange}
            options={options}
            placeholder="Add Buildings"
            value={value}
            {...props}
        />
    );
};

SitesTargetSelect.defaultProps = {
    disabled: false,
    onPatch:  () => {},
    values:   {
        targetBuildingTypes: [],
        targetSites:         []
    }
};

SitesTargetSelect.propTypes = {
    companyId: string.isRequired,
    disabled:  bool,
    onPatch:   func,
    values:    shape({
        targetBuildingTypes: array.isRequired,
        targetSites:         array.isRequired
    })
};

export default SitesTargetSelect;
